import { Container } from '../components/Container'
import SEO from '../components/SEO'
import { getCanonicalUrl } from '../utils/url'

export default function About() {
  return (
    <Container>
      <SEO 
        title="About ReikiNearMe.co - Our Mission & Values"
        description="Learn about ReikiNearMe.co's mission to connect individuals with qualified Reiki practitioners. Discover our values, commitment to holistic healing, and vision for accessible energy healing."
        canonicalUrl={getCanonicalUrl('/about')}
      />
      
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-center mb-12 text-primary-600 dark:text-primary-400">
          About ReikiNearMe.co
        </h1>

        {/* Mission Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">Our Mission</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            ReikiNearMe.co was created with a simple but powerful mission: to connect individuals seeking holistic healing with qualified Reiki practitioners in their local communities. We believe that everyone deserves access to the gentle, transformative power of Reiki, and we're committed to making that connection as seamless as possible.
          </p>
        </section>

        {/* Who We Are Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">Who We Are</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
            Founded in 2025 by a team of Reiki masters and wellness advocates, ReikiNearMe.co emerged from our own challenges in finding trusted practitioners when traveling or moving to new areas. We understand the importance of finding the right energy healer - someone who resonates with your needs, preferences, and healing journey.
          </p>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            Our team combines expertise in Reiki practice, holistic health, and technology to create a platform that serves both practitioners and clients with integrity and care.
          </p>
        </section>

        {/* What We Offer Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white">What We Offer</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* For Those Seeking Healing */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-4 text-primary-600 dark:text-primary-400">
                For Those Seeking Healing
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">•</span>
                  <span className="text-gray-600 dark:text-gray-300">
                    <strong className="text-gray-900 dark:text-white">Comprehensive Directory:</strong>{' '}
                    Browse our extensive network of verified Reiki practitioners sorted by location, specialty, and client reviews.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">•</span>
                  <span className="text-gray-600 dark:text-gray-300">
                    <strong className="text-gray-900 dark:text-white">Educational Resources:</strong>{' '}
                    Learn about the history, principles, and benefits of Reiki therapy through our regularly updated articles and guides.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">•</span>
                  <span className="text-gray-600 dark:text-gray-300">
                    <strong className="text-gray-900 dark:text-white">Community Stories:</strong>{' '}
                    Read testimonials and healing journeys from others who have experienced the benefits of Reiki.{' '}
                    <span className="text-primary-600 dark:text-primary-400 font-medium">(coming soon!)</span>
                  </span>
                </li>
              </ul>
            </div>

            {/* For Reiki Practitioners */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-4 text-primary-600 dark:text-primary-400">
                For Reiki Practitioners
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">•</span>
                  <span className="text-gray-600 dark:text-gray-300">
                    <strong className="text-gray-900 dark:text-white">Enhanced Visibility:</strong>{' '}
                    Showcase your practice, specialties, and credentials to a targeted audience actively seeking Reiki services.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">•</span>
                  <span className="text-gray-600 dark:text-gray-300">
                    <strong className="text-gray-900 dark:text-white">Professional Profile:</strong>{' '}
                    Create a detailed profile highlighting your training, approach, and the unique qualities you bring to your practice.{' '}
                    <span className="text-primary-600 dark:text-primary-400 font-medium">(coming soon!)</span>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Our Values Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white">Our Values</h2>
          <div className="flex flex-col items-center">
            {/* First Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full mb-6">
              {[
                {
                  title: 'Authenticity',
                  description: 'We verify credentials and encourage honest reviews to maintain the integrity of our platform.'
                },
                {
                  title: 'Inclusivity',
                  description: 'We welcome practitioners from all legitimate Reiki lineages and traditions.'
                },
                {
                  title: 'Accessibility',
                  description: 'We strive to make Reiki accessible to everyone, regardless of location or experience level.'
                }
              ].map((value, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold mb-2 text-primary-600 dark:text-primary-400">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">{value.description}</p>
                </div>
              ))}
            </div>
            
            {/* Second Row - Centered */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-[67%]">
              {[
                {
                  title: 'Education',
                  description: 'We believe in empowering individuals with knowledge about energy healing and its benefits.'
                },
                {
                  title: 'Respect',
                  description: "We honor the diverse paths to healing and the personal nature of each individual's wellness journey."
                }
              ].map((value, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold mb-2 text-primary-600 dark:text-primary-400">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Commitment Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">Our Commitment</h2>
          <div className="prose dark:prose-invert max-w-none">
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              At ReikiNearMe.co, we're more than just a directory - we're a community dedicated to fostering healing connections. We continuously improve our platform based on feedback from both practitioners and clients, ensuring we meet the evolving needs of our community.
            </p>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Whether you're taking your first steps into the world of energy healing or you're a seasoned practitioner looking to expand your reach, ReikiNearMe.co is here to support your journey.
            </p>
          </div>
        </section>

        {/* Connect With Us Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">Connect With Us</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
            Have questions, suggestions, or feedback? We'd love to hear from you! Reach out to our team at{' '}
            <a 
              href="mailto:contact@reikinearme.co"
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              contact@reikinearme.co
            </a>{' '}
            or follow us on social media for the latest updates, articles, and community events.
          </p>
        </section>

        {/* Closing Statement */}
        <p className="text-center text-lg italic text-gray-600 dark:text-gray-300">
          Bringing the healing power of Reiki to your doorstep, one connection at a time.
        </p>
      </div>
    </Container>
  )
} 